import React, { useState, useMemo } from 'react';
import { Segment } from 'tea-component';
import moment from 'moment';

import CardSlot from '@/pages/dashboard/components/CardSlot';
import {
  formatDataByChartType,
  addChartLabel,
  xAxisHighLight,
  xAxisLabelFormat,
} from '@/pages/dashboard/components/Chart/_config/plug';
import { useLocal, useWeekDay } from '@/pages/dashboard/utils/customHook';
import BarChart from '@/pages/dashboard/components/Chart/components/BarChart';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import MiniTable from '@/pages/dashboard/components/MiniTable';
import Abnormal from '@/pages/dashboard/components/Abnormal';
import {
  formatNum,
  getChartDateFormatString,
  formatPercentNum,
  unitConversion,
  unitConversionNew,
  handleDownload,
  handleDate,
} from '@/pages/dashboard/utils/utils';
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import { momentFormat } from '@/pages/dashboard/utils/_config/filtersTimeFormat';

import useMetrics from './useMetrics';
interface Props {
  data: any;
  loading: boolean;
  error: boolean;
  timeType: string;
}

let listData1: any = [
  { text: 'Install', value: 'install' },
  { text: 'Register', value: 'register' },
  { text: 'D2', value: 'd2' },
  { text: 'D3', value: 'd3' },
  { text: 'D7', value: 'd7' },
];
// 堆积图列表
const stackBarList = ['install', 'register'];

const EngagementTrends = (props: Props) => {
  const { data, loading, error, timeType } = props;
  const hasMonth = timeType === 'Month';
  const { getWeekDay } = useWeekDay();
  const { locale, getLocalText } = useLocal();
  const [chartType, setChartType] = useState('column-chart');
  const [activeTab, setActiveTab] = useState(listData1[0].value);
  const [iseyes, setIseyes] = useState(true);

  const allMetrics = useMetrics();
  const currentMetrics =
    (allMetrics?.find((item: any) => item?.key === activeTab) as any) ?? {};
  listData1 = listData1?.map((listItem: any) => {
    const target = allMetrics?.find(
      (item: any) => item?.key === listItem?.value,
    );
    return {
      ...listItem,
      text: target?.name,
    };
  });

  const getTableColumns = (keyArr: string[]) => [
    {
      key: 'date',
      header: getLocalText('common_date'),
      align: 'left',
      fixed: 'left',
      width: 100,
      render: (tr: any) => {
        const weekDay = getWeekDay(tr?.date);
        const isSunOrSat =
          weekDay === 'Sun' ||
          weekDay === 'Sat' ||
          weekDay === '周日' ||
          weekDay === '周六';
        return (
          <>
            <span className="table-text">
              {hasMonth ? momentFormat(tr.date, 'YYYY-MM') : tr.date}
            </span>
            {timeType !== 'Month' && (
              <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>
                {weekDay}
              </span>
            )}
          </>
        );
      },
    },
    {
      key: 'total',
      header: getLocalText('common_total'),
      align: 'right',
      width: 100,
      render: (tr: any) => <span>{tr.total}</span>,
    },
    ...keyArr.map((key) => ({
      key: key.toLowerCase(),
      header: key,
      align: 'right',
      width: 100,
      render: (tr: any) => <span>{tr[key?.toLowerCase()]}</span>,
    })),
  ];

  const getTableColumnsNoSum = (key: string, name: string) => [
    {
      key: 'date',
      header: getLocalText('common_date'),
      align: 'left',
      fixed: 'left',
      width: 100,
      render: (tr: any) => {
        const weekDay = getWeekDay(tr?.date);
        const isSunOrSat =
          weekDay === 'Sun' ||
          weekDay === 'Sat' ||
          weekDay === '周日' ||
          weekDay === '周六';
        return (
          <>
            <span className="table-text">
              {hasMonth ? momentFormat(tr.date, 'YYYY-MM') : tr.date}
            </span>
            {timeType !== 'Month' && (
              <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>
                {weekDay}
              </span>
            )}
          </>
        );
      },
    },
    {
      key: key.toLowerCase(),
      header: name,
      align: 'right',
      width: 100,
      render: (tr: any) => <span>{tr[key?.toLowerCase()]}</span>,
    },
  ];

  const { chart: chartOptions, table: tableOption } = useMemo(() => {
    const { stack, name: legendName } = currentMetrics;

    if (stackBarList?.includes(activeTab)) {
      const legendList = ['Organic', 'Others', 'UA'];

      // 图表配置
      const chart = legendList?.map((name: string) => {
        const legendData = data?.map((item: any) => ({
          x: item?.date,
          y: item?.[activeTab]?.find(
            (item: any) => item?.net_work_name === name,
          )?.value,
        }));

        return {
          name,
          stack,
          barCategoryGap: '50%',
          data: legendData,
        };
      });

      // 表格配置
      const tableData = data?.map((item: any) => {
        const dataMap: any = {};
        let total = 0;
        item?.[activeTab]?.forEach((legendItem: any) => {
          total += Number(legendItem?.value);
          dataMap[legendItem?.net_work_name?.toLowerCase()] = formatNum(
            legendItem?.value,
            false,
            true
          );
        });
        return {
          date: handleDate(item?.date,timeType?.toUpperCase()),
          total: formatNum(total, false, true),
          ...dataMap,
        };
      });

      return {
        chart,
        table: {
          option: getTableColumns(legendList),
          data: tableData,
        },
      };
    } 
    // 图表配置
    const chart = [
      {
        name: legendName,
        barCategoryGap: '50%',
        data: data?.map((item: any) => ({
          x: item?.date,
          y: item?.[activeTab],
        })),
      },
    ];

    // 表格配置
    const tableData = data?.map((item: any) => ({
      date: handleDate(item?.date,timeType?.toUpperCase()),
      [activeTab]: formatPercentNum(item?.[activeTab]),
    }));

    return {
      chart,
      table: {
        option: getTableColumnsNoSum(activeTab, legendName),
        data: tableData,
      },
    };
    
  }, [data, activeTab, locale]);

  const download = () => {
    const name = getLocalText('ua_engagementTrends')?.replace(/\u0020/g, '_');
    const activeTabName = getLocalText(`ua_${activeTab}`);
    // 导出excel
    handleDownload(tableOption,`${name}_${activeTabName}`);
  };

  function formatterFunc(param: any) {
    const { isPercent } = currentMetrics;
    const data: Array<{}> = [];
    let sumNum = 0;
    for (let i = 0;i < param.length;i++) {
      const { color, seriesName } = param[i];

      const value = isPercent
        ? formatPercentNum(param[i].value)
        : formatNum(param[i].value, false, true);
      sumNum += Number(String(value).replace(',', ''));
      data.push({
        name: seriesName,
        metricsNum: value,
        color,
      });
    }

    return getEchartTooltipText({
      title: moment(`${param[0].axisValue}`).format(timeType === 'Month' ? 'YYYY-MM' : 'YYYY-MM-DD'),
      data,
      hasSum: stackBarList?.includes(activeTab),
      sumName: getLocalText('common_total'),
      sumNum: formatNum(sumNum, false, true),
    });
  }

  const handleAxisFormat = (value: any) => timeType === 'Month'
    ? moment(value).format('YYYY-MM')
    : moment(value).format('MM-DD');

  const axisLabelFormat = (opt: any, timeType: any) => opt.xAxis?.map((item: any) => ({
    ...item,
    axisLabel: {
      ...item.axisLabel,
      formatter: (value: any) => getChartDateFormatString(timeType, value),
    },
  }));

  const setOtherConfigFunc = (opt: any, timeType: any) => {
    const { isPercent, stack } = currentMetrics;
    opt.xAxis = axisLabelFormat(opt, timeType);
    const yAxisLabelFormat = (value: any) => {
      const formatLabel = value ? unitConversion(value, true) : value;
      const num =
        value < 1 && value > 0 ? parseFloat(value.toFixed(3)) : formatLabel;
      return num;
    };
    opt.yAxis.axisLabel.formatter = (value: any) => {
      if (!isPercent) return yAxisLabelFormat(value);
      if (value > 0 && value < 0.1 && String(value).length > 4)
        return formatPercentNum(+value, 1);
      return formatPercentNum(value, 0);
    };

    return addChartLabel(opt, {
      formatter: (params: any) => {
        const result = isPercent
          ? formatPercentNum(params?.value)
          : formatNum(params?.value, false, false);

        if (!isPercent && result?.length >= 9) {
          return unitConversionNew(params?.value, {
            isFloat: true,
            fixedNum: 3,
          });
        }

        return result;
      },
      show: ['d2', 'd3', 'd7'].includes(activeTab) && iseyes,
      formatNum: (value: any) => {
        if (isPercent) return formatPercentNum(value);
        return formatNum(value, false, false);
      },
    });
  };

  const barOptions = formatDataByChartType('bar', chartOptions);
  const lineOptions = formatDataByChartType('line', chartOptions);

  const handleTypeChange = (type: any) => {
    if (type === 'eye') {
      return setIseyes(!iseyes);
    }
    setChartType(type);
  };

  const chartMap: any = {
    'column-chart': {
      content: getLocalText('common_bar_chart'),
      component: (
        <BarChart
          height={340}
          legendData={barOptions?.legend ?? []}
          xAxisData={barOptions?.xAxisData ?? []}
          series={barOptions?.series ?? []}
          formatterFunc={formatterFunc}
          setOtherConfigFunc={(opt: any) => {
            opt = xAxisLabelFormat(opt, { xAxisLabelFormat: handleAxisFormat });
            opt = xAxisHighLight(opt, {
              formatter: (params: { value: string; }) => handleAxisFormat(params.value),
            });
            return setOtherConfigFunc(opt, timeType);
          }}
        />
      ),
    },
    'discount-chart': {
      content: getLocalText('common_line_chart'),
      component: (
        <LineChart
          height={340}
          legendData={lineOptions?.legend ?? []}
          xAxisData={lineOptions?.xAxisData ?? []}
          series={lineOptions?.series ?? []}
          formatterFunc={formatterFunc}
          setOtherConfigFunc={(opt: any) => {
            opt = xAxisLabelFormat(opt, { xAxisLabelFormat: handleAxisFormat });
            opt = setOtherConfigFunc(opt, timeType);
            opt = xAxisHighLight(opt, {
              formatter: (params: { value: string; }) => handleAxisFormat(params.value),
            });
            return opt;
          }}
        />
      ),
    },
    sheet: {
      content: getLocalText('common_table'),
      component: (
        <MiniTable
          loading={false}
          height={340}
          tableOption={tableOption?.option}
          tableList={tableOption?.data}
          recordKey={'date'}
          scrolLableVal={{
            virtualizedOptions: {
              height: 296,
              itemHeight: 50,
            },
          }}
        />
      ),
    },
  };

  const dataNotEmptyArr = data?.filter((item) => item[activeTab] !== '') || [];
  const isNoData = data?.length === 0 || dataNotEmptyArr?.length === 0;

  return (
    <CardSlot
      getChange={handleTypeChange}
      title={getLocalText('ua_engagementTrends')}
      iconList={[
        { icon: 'column-chart', context: getLocalText('common_bar_chart') },
        { icon: 'discount-chart', context: getLocalText('common_line_chart') },
        { icon: 'sheet', context: getLocalText('common_table') },
      ]}
      iconShow={!data || data?.length === 0}
      activeTab={chartType}
      isEyes={
        !(['d2', 'd3', 'd7'].includes(activeTab) && chartType !== 'sheet')
      }
      onDownload={download}
    >
      <Segment
        className="overlay-line-tab"
        value={activeTab}
        onChange={(value: any) => {
          if (
            stackBarList?.includes(value) &&
            !stackBarList?.includes(activeTab)
          ) {
            setChartType('column-chart');
          }

          if (
            ['d2', 'd3', 'd7']?.includes(value) &&
            stackBarList?.includes(activeTab)
          ) {
            setChartType('discount-chart');
          }
          setActiveTab(value);
        }}
        options={listData1}
      />
      <Abnormal
        style={{ height: '340px' }}
        component={chartMap?.[chartType]?.component}
        isLoading={loading}
        isError={error}
        isNoData={isNoData}
      />
    </CardSlot>
  );
};

export default EngagementTrends;
